import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { BlogCard } from "@/components/site/cards";
import { ContactCTA } from "@/pages/Home";
import { Clock, ArrowLeft } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
    api.get("/blog", { params: { limit: 3 } }).then((r) => setRelated(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-wv flex min-h-[70vh] flex-col items-center justify-center text-center">
        <h1 className="font-heading text-3xl font-semibold text-white">Article not found</h1>
        <Link to="/blog" className="btn-primary mt-6">Back to blog</Link>
      </div>
    );
  }
  if (!post) return <div className="min-h-[70vh]" />;

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    author: { "@type": "Person", name: post.author },
    publisher: { "@type": "Organization", name: "Web Vantage Enterprises" },
  };

  return (
    <>
      <SEO title={post.title} description={post.excerpt} image={post.cover_image} path={`/blog/${slug}`} schema={schema} />
      <PageHero
        overline={post.category}
        title={post.title}
        crumbs={[{ label: "Blog", to: "/blog" }, { label: post.title }]}
      />
      <article className="py-20 lg:py-28">
        <div className="container-wv max-w-3xl">
          <div className="mb-8 flex items-center gap-4 text-sm text-slate-400">
            <span>By {post.author}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {post.read_time} min read</span>
          </div>
          <Reveal className="overflow-hidden rounded-3xl border border-white/10">
            <img src={post.cover_image} alt={post.title} className="w-full object-cover" />
          </Reveal>
          <div className="prose-invert mt-10 space-y-6 text-base leading-relaxed text-slate-300">
            {post.content.split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap gap-2">
            {post.tags?.map((t) => <span key={t} className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-400">#{t}</span>)}
          </div>
          <div className="mt-12">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> All articles
            </Link>
          </div>
        </div>
        <div className="container-wv mt-20">
          <h2 className="mb-8 font-heading text-2xl font-semibold text-white">More insights</h2>
          <div className="grid gap-8 md:grid-cols-3">
            {related.filter((r) => r.slug !== slug).slice(0, 3).map((r) => <BlogCard key={r.id} post={r} />)}
          </div>
        </div>
      </article>
      <ContactCTA />
    </>
  );
}
